/** @format */

import { Component, Input, OnInit } from "@angular/core";
import { ToastService } from "mis-crystal-design-system/toast";
import { Moment, parseZone } from "moment-timezone";
import { Observable, throwError, combineLatest, ReplaySubject } from "rxjs";
import { catchError, map, take, switchMap } from "rxjs/operators";
import { AsyncWidget } from "mis-crystal-design-system/widgets";
import { DrawerService } from "mis-design-system/drawer";
import { NsWISConfigurations } from "../../models/configurations.model";
import { NsEmpWebExpData } from "../../models/emp-web-exp.model";
import { ApiService } from "../../services/api.service";
import { BookingDataService } from "../../services/booking-data.service";
import { ConfigurationService } from "../../services/configuration.service";
import { EmpUserService } from "../../services/emp-user.service";
import { LoaderService } from "../../services/loader.service";
import { UpcomingBookingsComponent } from "../modals/upcoming-bookings/upcoming-bookings.component";
import { minLength } from "../../functions/booking-utils.functions";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../constants/assets.constants";

@Component({
  selector: "mis-employee-web-exp-history",
  templateUrl: "./history.component.html",
  styleUrls: ["./history.component.scss"]
})
export class HistoryComponent implements OnInit {
  @Input() buid: string = "qa-Test";
  @Input() empGuid: string = "EMqaTest-$000-0000-0000-000000000001";
  configurations: NsWISConfigurations.IConfigurations;
  widget: AsyncWidget;
  dateRangeValue: [Date, Date] = [new Date(), new Date()];
  dateRangePickerConfig = {};
  dateRangeStream = new ReplaySubject<[Moment, Moment]>(1);
  ASSETS = CONSTANTS_ASSETS_EMP_WEB_EXP;
  bookingsLength: number = 0;
  constructor(
    private user: EmpUserService,
    private configSvc: ConfigurationService,
    private bookingSvc: BookingDataService,
    private apiService: ApiService,
    private toast: ToastService,
    private loader: LoaderService,
    private drawerSvc: DrawerService
  ) {}

  ngOnInit(): void {
    if (!this.empGuid || !this.buid) {
      throw new Error("missing required inputs buid & empGUid");
    }
    this.loader.show();
    this.user.setUser(this.buid, this.empGuid);
    this.configSvc.refreshReplayStreams();
    this.bookingSvc.refreshReplayStreams();
    combineLatest([
      this.apiService.getEmpTimezone().pipe(
        catchError(err => {
          this.loader.hide();
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load timezone. ${JSON.stringify(err)}`);
        })
      ),
      this.configSvc.configurations.pipe(
        catchError(err => {
          this.loader.hide();
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load Configurations. ${JSON.stringify(err)}`);
        })
      )
    ]).subscribe(([timezone, configs]) => {
      this.configurations = configs;
      this.loader.hide();
      this.bookingSvc.timezone = timezone;
      const start = parseZone().tz(timezone).subtract(7, "days");
      const end = parseZone().tz(timezone).subtract(1, "days");
      this.dateRangePickerConfig = {
        containerClass: "booking-history-date-range-picker theme-green",
        minDate: parseZone().tz(timezone).subtract(30, "days").startOf("day").toDate(),
        maxDate: end.toDate(),
        rangeInputFormat: configs.flattenedSettings.DATE_FORMAT?.replace(/y/g, "Y")
        // maxDateRange: 6,
      };
      this.dateRangeValue = [start.toDate(), end.toDate()];
      this.dateRangeStream.next([start, end]);
      this.widget = new AsyncWidget(this.genUpcomingBookingStream(), [minLength(0)]);
    });
  }

  private genUpcomingBookingStream(): Observable<NsEmpWebExpData.IList[]> {
    return this.dateRangeStream.pipe(
      take(1),
      switchMap(([start, end]) => {
        return this.apiService
          .getEmpProfile(
            this.apiService.genEmpDetailQuery(start.startOf("day").valueOf(), end.endOf("day").valueOf(), [
              "OFFICE",
              "WFH",
              "PARKING_TWO",
              "PARKING_FOUR",
              "MEALS"
            ])
          )
          .pipe(
            map(res => {
              this.bookingsLength = 0;
              if (res.dataPresent) {
                const bookings = this.bookingSvc.organiseBookings(
                  res?.data?.getEmployee?.[0]?.bookingDetailDTOS || [],
                  this.configurations.flattenedSettings.approvalFlowEnabled
                );
                this.bookingsLength = bookings.length;
                return bookings;
              } else {
                this.toast.displayMsg(res.errors?.[0]?.message || "Failed to load bookings!", 5000);
                return [];
              }
            })
          );
      })
    );
  }

  public handleUpcoming(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    this.drawerSvc.show(UpcomingBookingsComponent, { booking, configs: this.configurations });
  }

  public changeDateRange(dateRangeValue) {
    const [start, end] = dateRangeValue;
    if (parseZone(end).diff(parseZone(start), "days") >= 7) {
      this.toast.displayMsg("You can select maximum of 7 days at a time.", 3000);
      this.dateRangeValue = [new Date(this.dateRangeValue[0]), new Date(this.dateRangeValue[1])];
      return;
    }
    if (dateRangeValue === this.dateRangeValue) return;
    this.dateRangeValue = dateRangeValue;
    this.dateRangeStream.next([parseZone(start), parseZone(end)]);
    this.widget?.reset();
    this.widget?.toggleLoader(true);
  }
}
